/** @jsxImportSource @emotion/react */

import { useScanEditingContextSlice } from '@/Client/State';
import type { ScanGroupOptimized, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';

import { OVERVIEW_HEADER_BOX_STYLE } from './focusedOverviewHeaderStyles';
import { FocusedTaskChangeOrderBanner } from './FocusedTaskChangeOrderBanner';
import { FocusedTaskEditCardHeader } from './FocusedTaskEditCardHeader';
import { FocusedTaskFields } from './FocusedTaskEditor';

/**
 * The task's overview box — the same white, bordered box the group and scope headers draw, so the
 * editor reads the same at every scope instead of the task arriving as one card wrapped around the
 * whole panel.
 *
 * What is inside differs, as it must: a group summarises its tasks, whereas a task has fields to
 * edit — the contractor, its template's values, and the description folded under a hairline. Its
 * options are not here — they are a section of their own below Notes, the way Photos and Notes
 * are. See FocusedTaskOptionsSection.
 */
export const FocusedTaskOverviewHeader = ({
  baselineTask,
  group,
  groupId,
  task,
  taskIsEditRequested,
}: {
  baselineTask?: ScanTaskOptimized;
  group?: ScanGroupOptimized;
  groupId: string;
  task: ScanTaskOptimized;
  taskIsEditRequested?: boolean;
}) => {
  const { overallTaskStatusByTaskId } = useScanEditingContextSlice(['overallTaskStatusByTaskId']);

  return (
    <div css={OVERVIEW_HEADER_BOX_STYLE} data-testid={'focused-task-overview-header'}>
      <FocusedTaskChangeOrderBanner />
      <FocusedTaskEditCardHeader
        baselineTask={baselineTask}
        group={group}
        groupId={groupId}
        task={task}
        taskIsEditRequested={taskIsEditRequested}
        overallStatus={overallTaskStatusByTaskId?.[task.id] ?? 'draft'}
      />
      <FocusedTaskFields
        baselineTask={baselineTask}
        task={task}
        groupId={groupId}
      />
    </div>
  );
};
